import React, { useState, useEffect } from 'react';
import {
  FaTruck,
  FaRoute,
  FaClock,
  FaUserCheck,
  FaRupeeSign,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaStore,
  FaMedal,
  FaSync,
  FaFileExcel
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { config } from '../utils/config';
import { safeStorage } from '../utils/storageHelper';
import FleetDataEntry from './FleetDataEntry';

const FleetAnalytics = () => {
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayDate());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [hoveredKm, setHoveredKm] = useState(null);
  const [selectedHour, setSelectedHour] = useState('11-12');
  const [hourlyDateFrom, setHourlyDateFrom] = useState(getFirstDayOfMonth());
  const [hourlyDateTo, setHourlyDateTo] = useState(getTodayDate());
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [isAddOrderActive, setIsAddOrderActive] = useState(false);

  const handleRefresh = () => {
    const defaultFrom = getFirstDayOfMonth();
    const defaultTo = getTodayDate();
    setDateFrom(defaultFrom);
    setDateTo(defaultTo);
    setHourlyDateFrom(defaultFrom);
    setHourlyDateTo(defaultTo);
    setDateError(null);
    setExportError(null);
    setData(null);
    setHourlyData(null);
    fetchFleetStats(defaultFrom, defaultTo);
    fetchHourlyDrilldown(defaultFrom, defaultTo, selectedHour);
    setRefreshKey(prev => prev + 1);
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthIdx = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${months[monthIdx]} ${year}`;
      }
    }
    return dateStr;
  };

  const isValidDateStr = (d) => {
    if (!d || typeof d !== 'string') return false;
    const trimmed = d.trim();
    if (trimmed.length !== 10) return false;
    const parts = trimmed.split('-');
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    return year >= 2000 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31;
  };

  const fetchFleetStats = async (fromDate, toDate) => {
    setLoading(true);
    setError(null);
    try {
      const token = safeStorage.getItem('authToken');
      const res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/fleet/statistics/?date_from=${fromDate}&date_to=${toDate}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) {
        throw new Error(`Failed to load statistics (${res.status})`);
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching fleet stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!data) {
      setExportError('No fleet data available to export.');
      return;
    }
    if (exportingExcel) return;
    setExportingExcel(true);
    setExportError(null);
    try {
      const workbook = XLSX.utils.book_new();

      // 1. Overview KPIs Sheet
      const kpiRows = [
        { 'Metric': 'Total Orders', 'Value': data.kpis?.totalOrders ?? 0 },
        { 'Metric': 'Total Distance (KM)', 'Value': data.kpis?.totalKm ?? 0 },
        { 'Metric': 'Overtime Hours', 'Value': data.kpis?.otHours ?? 0 },
        { 'Metric': 'Total Riders (Status=1)', 'Value': data.kpis?.totalRiders ?? 0 },
        { 'Metric': 'Active Available Riders', 'Value': data.kpis?.activeRiders ?? 0 },
      ];
      const kpiSheet = XLSX.utils.json_to_sheet(kpiRows);
      kpiSheet['!cols'] = [{ wch: 28 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, kpiSheet, 'Overview KPIs');

      // 2. Hourly Summary & Drilldown Data
      let currentHourlyData = hourlyData;
      if (!currentHourlyData) {
        try {
          const token = safeStorage.getItem('authToken');
          const hRes = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/fleet/hourly-drilldown/?date_from=${hourlyDateFrom}&date_to=${hourlyDateTo}&selected_hour=${selectedHour}`, {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json'
            }
          });
          if (hRes.ok) {
            currentHourlyData = await hRes.json();
          }
        } catch (e) {
          console.error('Failed to fetch hourly data for export:', e);
        }
      }

      if (currentHourlyData && Array.isArray(currentHourlyData.hourly_summary) && currentHourlyData.hourly_summary.length > 0) {
        const hourlySummaryRows = currentHourlyData.hourly_summary.map((h) => ({
          'Hour Window': h.hour,
          'Total Orders Delivered': h.orders,
          'Active Merchants Count': h.active_merchants ?? 0,
          'Active Merchant Names': h.active_merchant_names || (Array.isArray(h.active_merchants_list) ? h.active_merchants_list.join(', ') : '-'),
          'Active Riders Count': h.active_riders ?? 0,
          'Active Rider Names': h.active_rider_names || (Array.isArray(h.active_riders_list) ? h.active_riders_list.join(', ') : '-')
        }));
        const hourlySummarySheet = XLSX.utils.json_to_sheet(hourlySummaryRows);
        hourlySummarySheet['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 45 }, { wch: 20 }, { wch: 45 }];
        XLSX.utils.book_append_sheet(workbook, hourlySummarySheet, 'Hourly Summary');
      }

      if (currentHourlyData) {
        const stackedRows = [];
        const hoursList = (Array.isArray(currentHourlyData.all_hours_drilldown) && currentHourlyData.all_hours_drilldown.length > 0)
          ? currentHourlyData.all_hours_drilldown
          : (currentHourlyData.drilldown ? [currentHourlyData.drilldown] : []);

        hoursList.forEach((hDrill) => {
          const hourLabel = hDrill.hour;
          const activeMerchants = (hDrill.by_merchant || []).filter(m => m.orders > 0);
          const activeRiders = (hDrill.by_rider || []).filter(r => r.orders > 0);
          const totalOrders = hDrill.total_orders ?? 0;

          // 1. Hour Window Header Row
          stackedRows.push({
            'Hour Window': `=== HOURLY WINDOW: ${hourLabel} ===`,
            'Type': `Total Orders: ${totalOrders}`,
            'Name': `Active Merchants: ${activeMerchants.length}`,
            'Orders Delivered': `Active Riders: ${hDrill.active_riders ?? activeRiders.length}`
          });

          if (activeMerchants.length === 0 && activeRiders.length === 0) {
            stackedRows.push({
              'Hour Window': hourLabel,
              'Type': 'Summary',
              'Name': 'No Active Deliveries',
              'Orders Delivered': 0
            });
          } else {
            // Merchants Sub-List
            activeMerchants.forEach((m) => {
              stackedRows.push({
                'Hour Window': hourLabel,
                'Type': 'Merchant',
                'Name': m.name || `Merchant #${m.id}`,
                'Orders Delivered': m.orders
              });
            });

            // Riders Sub-List
            activeRiders.forEach((r) => {
              stackedRows.push({
                'Hour Window': hourLabel,
                'Type': 'Rider',
                'Name': r.name || `Rider #${r.id}`,
                'Orders Delivered': r.orders
              });
            });
          }

          // Empty Row Separator between Hour Windows
          stackedRows.push({
            'Hour Window': '',
            'Type': '',
            'Name': '',
            'Orders Delivered': ''
          });
        });

        if (stackedRows.length > 0) {
          const stackedSheet = XLSX.utils.json_to_sheet(stackedRows);
          stackedSheet['!cols'] = [{ wch: 32 }, { wch: 22 }, { wch: 35 }, { wch: 22 }];
          XLSX.utils.book_append_sheet(workbook, stackedSheet, 'Hourly Breakdown');
        }
      }

      // 2. Rider Leaderboard Sheet
      if (Array.isArray(data.riderLeaderboard) && data.riderLeaderboard.length > 0) {
        const leaderboardRows = data.riderLeaderboard.map((r, idx) => ({
          'Rank': idx + 1,
          'Rider ID': r.riderId,
          'Rider Name': r.riderName || `Rider #${r.riderId}`,
          'Status': r.isAvailable || r.status === 'Available' || r.status === 1 ? 'Available' : 'Offline',
          'Orders Completed': r.orders ?? 0,
          'Distance (KM)': r.km ?? 0,
          'OT Hours': r.otHours ?? 0
        }));
        const leaderboardSheet = XLSX.utils.json_to_sheet(leaderboardRows);
        leaderboardSheet['!cols'] = [{ wch: 8 }, { wch: 12 }, { wch: 25 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(workbook, leaderboardSheet, 'Rider Leaderboard');
      }

      // 3. Merchant Distribution Sheet
      if (Array.isArray(data.merchantBreakdown) && data.merchantBreakdown.length > 0) {
        const merchantRows = data.merchantBreakdown.map((m) => ({
          'Merchant ID': m.merchantId,
          'Merchant Name': m.merchantName,
          'Orders': m.orders,
          'Share (%)': `${m.sharePct}%`
        }));
        const merchantSheet = XLSX.utils.json_to_sheet(merchantRows);
        merchantSheet['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(workbook, merchantSheet, 'Merchant Distribution');
      }

      // 4. 7-Day Trend Sheet
      if (Array.isArray(data.trend) && data.trend.length > 0) {
        const trendRows = data.trend.map((t) => ({
          'Date': t.date,
          'Day': t.label,
          'Orders': t.orders,
          'Distance (KM)': t.km
        }));
        const trendSheet = XLSX.utils.json_to_sheet(trendRows);
        trendSheet['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(workbook, trendSheet, '7-Day Trend');
      }

      // 5. Fleet Operational Data Grid Sheet
      try {
        const token = safeStorage.getItem('authToken');
        const res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/fleet/entries/?date_from=${dateFrom}&date_to=${dateTo}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const gridData = await res.json();
          const riders = gridData.riders || [];
          const merchants = gridData.merchants || [];
          const entries = gridData.entries || [];

          const entryMap = {};
          entries.forEach(e => { entryMap[e.riderId] = e; });

          const gridRows = riders.map(r => {
            const entry = entryMap[r.id] || {};
            const row = {
              'Rider ID': r.id,
              'Rider Name': r.name,
              'Attendance': entry.attendance || 'P',
              'Total Orders': entry.orders ?? 0,
              'Distance (KM)': entry.km ?? 0,
              'OT Hours': entry.otHours ?? 0,
            };
            merchants.forEach(m => {
              row[m.name || m.business_name || `Merchant #${m.id}`] = (entry.merchantOrders && entry.merchantOrders[m.id]) || 0;
            });
            return row;
          });

          if (gridRows.length > 0) {
            const gridSheet = XLSX.utils.json_to_sheet(gridRows);
            XLSX.utils.book_append_sheet(workbook, gridSheet, 'Operational Data Grid');
          }
        }
      } catch (err) {
        console.error('Failed to include operational grid in excel:', err);
      }

      XLSX.writeFile(workbook, `Fleet_Operations_Report_${dateFrom}_to_${dateTo}.xlsx`);
    } catch (err) {
      console.error('Excel Export error:', err);
      setExportError(`Failed to export Excel report: ${err.message || 'An error occurred while generating the Excel file.'}`);
    } finally {
      setExportingExcel(false);
    }
  };

  const fetchHourlyDrilldown = async (fromDate, toDate, hour) => {
    setHourlyLoading(true);
    try {
      const token = safeStorage.getItem('authToken');
      let url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/fleet/hourly-drilldown/?date_from=${fromDate}&date_to=${toDate}`;
      if (hour) {
        url += `&selected_hour=${hour}`;
      }
      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const result = await res.json();
        setHourlyData(result);
        if (result.selected_hour) {
          setSelectedHour(result.selected_hour);
        }
      }
    } catch (err) {
      console.error('Error fetching hourly drilldown:', err);
    } finally {
      setHourlyLoading(false);
    }
  };

  const handleHourClick = (hourStr) => {
    setSelectedHour(hourStr);
    fetchHourlyDrilldown(hourlyDateFrom, hourlyDateTo, hourStr);
  };

  useEffect(() => {
    if (isValidDateStr(dateFrom) && isValidDateStr(dateTo)) {
      if (new Date(dateFrom) > new Date(dateTo)) {
        setDateError('From date cannot be later than To date. Please select a valid date range.');
        setLoading(false);
        return;
      }
      setDateError(null);
      fetchFleetStats(dateFrom, dateTo);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (isValidDateStr(hourlyDateFrom) && isValidDateStr(hourlyDateTo)) {
      if (new Date(hourlyDateFrom) > new Date(hourlyDateTo)) {
        return;
      }
      fetchHourlyDrilldown(hourlyDateFrom, hourlyDateTo, selectedHour);
    }
  }, [hourlyDateFrom, hourlyDateTo]);

  return (
    <div className="delivery-fleet-container" style={{ padding: '24px', background: '#F4F4F8', minHeight: '100vh' }}>
      {/* Header Bar */}
      {!isAddOrderActive && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2A2C41', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaTruck style={{ color: '#F55D00' }} /> Fleet Operations & Analytics
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', gap: '8px' }}>
              <FaCalendarAlt style={{ color: '#F55D00' }} />
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => {
                  const val = e.target.value;
                  setDateFrom(val);
                  setHourlyDateFrom(val);
                }}
                style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#2A2C41' }}
              />
              <span style={{ color: '#2A2C41', opacity: 0.5, fontSize: '13px', fontWeight: '500' }}>to</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => {
                  const val = e.target.value;
                  setDateTo(val);
                  setHourlyDateTo(val);
                }}
                style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#2A2C41' }}
              />
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading || hourlyLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, #F55D00 0%, #FDBF50 100%)',
                color: '#fff',
                border: 'none',
                padding: '8px 18px',
                borderRadius: '8px',
                cursor: loading || hourlyLoading ? 'not-allowed' : 'pointer',
                opacity: loading || hourlyLoading ? 0.7 : 1,
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(245, 93, 0, 0.25)'
              }}
            >
              <FaSync className={loading || hourlyLoading ? "fa-spin" : ""} />
              {loading || hourlyLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || !data}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#2A2C41',
                color: '#fff',
                border: 'none',
                padding: '8px 18px',
                borderRadius: '8px',
                cursor: exportingExcel || !data ? 'not-allowed' : 'pointer',
                opacity: exportingExcel || !data ? 0.7 : 1,
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(42, 44, 65, 0.25)'
              }}
            >
              <FaFileExcel className={exportingExcel ? "fa-spin" : ""} style={{ color: '#FDBF50' }} />
              {exportingExcel ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>
      )}

      {!isAddOrderActive && exportError && (
        <div style={{ background: '#fff', border: '2px solid #F55D00', padding: '14px 18px', borderRadius: '8px', color: '#2A2C41', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaExclamationTriangle style={{ color: '#F55D00', fontSize: '18px' }} />
            <span><strong style={{ color: '#F55D00' }}>Export Failed:</strong> {exportError}</span>
          </div>
          <button onClick={() => setExportError(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '700', color: '#2A2C41', fontSize: '18px' }}>×</button>
        </div>
      )}
      {!isAddOrderActive && dateError && (
        <div style={{ background: '#fff', border: '2px solid #F55D00', padding: '16px', borderRadius: '8px', color: '#2A2C41', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaExclamationTriangle style={{ color: '#F55D00', fontSize: '20px' }} />
          <div>
            <strong style={{ color: '#F55D00' }}>Invalid Date Selection:</strong> {dateError}
          </div>
        </div>
      )}

      {!isAddOrderActive && !dateError && loading && !data && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#2A2C41' }}>
          <FaSync className="fa-spin" style={{ fontSize: '32px', marginBottom: '12px', color: '#F55D00' }} />
          <p style={{ fontWeight: '600' }}>Loading Fleet Operations Data...</p>
        </div>
      )}

      {!isAddOrderActive && !dateError && !loading && error && (
        <div style={{ background: '#fff', border: '2px solid #F55D00', padding: '16px', borderRadius: '8px', color: '#2A2C41', marginBottom: '24px' }}>
          <strong style={{ color: '#F55D00' }}>Error:</strong> {error}
        </div>
      )}

      {!isAddOrderActive && !dateError && !loading && !error && data && (
        <>
          {/* KPI Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2A2C41', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                TOTAL ORDERS DELIVERED <FaTruck style={{ color: '#F55D00', fontSize: '18px' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2A2C41', marginTop: '8px' }}>
                {data?.kpis?.totalOrders ?? 0}
              </div>
            </div>

            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2A2C41', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                TOTAL KM <FaRoute style={{ color: '#FDBF50', fontSize: '18px' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2A2C41', marginTop: '8px' }}>
                {data?.kpis?.totalKm ?? 0} <span style={{ fontSize: '14px', color: '#2A2C41', opacity: 0.6, fontWeight: '500' }}>km</span>
              </div>
            </div>

            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2A2C41', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                OVERTIME <FaClock style={{ color: '#F55D00', fontSize: '18px' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2A2C41', marginTop: '8px' }}>
                {data?.kpis?.otHours ?? 0} <span style={{ fontSize: '14px', color: '#2A2C41', opacity: 0.6, fontWeight: '500' }}>hrs</span>
              </div>
            </div>

            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2A2C41', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                TOTAL RIDERS <FaTruck style={{ color: '#2A2C41', fontSize: '18px' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2A2C41', marginTop: '8px' }}>
                {data?.kpis?.totalRiders ?? 0}
              </div>
            </div>

            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2A2C41', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                ACTIVE RIDERS <FaUserCheck style={{ color: '#FDBF50', fontSize: '18px' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2A2C41', marginTop: '8px' }}>
                {data?.kpis?.activeRiders ?? 0}
              </div>
            </div>
          </div>

          {/* HOURLY DRILLDOWN COMPONENT */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#2A2C41', display: 'flex', alignItems: 'center', gap: '8px' }}>
                HOURLY BREAKDOWN {hourlyLoading && <FaSync className="fa-spin" style={{ color: '#F55D00', fontSize: '12px' }} />}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F4F4F8', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px 10px' }}>
                <FaCalendarAlt style={{ color: '#F55D00', fontSize: '13px' }} />
                <input
                  type="date"
                  value={hourlyDateFrom}
                  max={hourlyDateTo}
                  onChange={(e) => setHourlyDateFrom(e.target.value)}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#2A2C41', fontSize: '13px' }}
                />
                <span style={{ color: '#2A2C41', opacity: 0.5, fontSize: '12px', fontWeight: '500' }}>to</span>
                <input
                  type="date"
                  value={hourlyDateTo}
                  min={hourlyDateFrom}
                  onChange={(e) => setHourlyDateTo(e.target.value)}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#2A2C41', fontSize: '13px' }}
                />
                <span style={{ color: '#2A2C41', opacity: 0.6, fontSize: '12px', fontWeight: '500' }}>- All figures</span>
              </div>
            </div>

            {/* Top Section: Deliveries by hour */}
            <div style={{ background: '#F4F4F8', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#2A2C41', margin: 0 }}>Deliveries by hour</h4>
                <span style={{ fontSize: '11px', color: '#2A2C41', opacity: 0.6, fontStyle: 'italic' }}>Click an hour to inspect</span>
              </div>

              {hourlyLoading && !hourlyData ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#2A2C41' }}>
                  <FaSync className="fa-spin" style={{ marginRight: '8px', color: '#F55D00' }} /> Loading hourly drilldown data...
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                  {(() => {
                    const summary = hourlyData?.hourly_summary || [];
                    const maxOrders = Math.max(...summary.map(s => s.orders), 1);
                    return summary.map((s) => {
                      const isSelected = selectedHour === s.hour || (hourlyData?.selected_hour === s.hour);
                      const pct = Math.max((s.orders / maxOrders) * 100, s.orders > 0 ? 8 : 4);
                      return (
                        <div
                          key={s.hour}
                          onClick={() => handleHourClick(s.hour)}
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          {/* 1. Count Zone */}
                          <div style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: isSelected ? '#F55D00' : '#2A2C41' }}>
                              {s.orders}
                            </span>
                          </div>

                          {/* 2. Bar Container Zone (100px fixed height, bottom-aligned) */}
                          <div style={{ height: '100px', width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                            <div
                              style={{
                                width: '100%',
                                height: `${pct}%`,
                                background: isSelected ? 'linear-gradient(180deg, #F55D00 0%, #FDBF50 100%)' : '#FDBF50',
                                opacity: isSelected ? 1 : 0.45,
                                borderRadius: '3px 3px 0 0',
                                transition: 'all 0.2s ease',
                                border: isSelected ? '1px solid #F55D00' : '1px solid #FDBF50'
                              }}
                            />
                          </div>

                          {/* 3. Label Zone */}
                          <div style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '6px' }}>
                            <span style={{ fontSize: '10px', fontWeight: '700', color: isSelected ? '#F55D00' : '#2A2C41', whiteSpace: 'nowrap' }}>
                              {s.hour}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Bottom Section: Side-by-Side Merchant & Rider Drilldown */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
              {/* Merchant Breakdown */}
              <div style={{ background: '#F4F4F8', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                  <h5 style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41', margin: 0 }}>
                    {hourlyData?.drilldown?.hour || selectedHour} - by merchant
                  </h5>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#F55D00' }}>
                    {hourlyData?.drilldown?.total_orders ?? 0} orders
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                  {(() => {
                    const merchants = hourlyData?.drilldown?.by_merchant || [];
                    if (merchants.length === 0) {
                      return <div style={{ fontSize: '12px', color: '#2A2C41', opacity: 0.6, textAlign: 'center', padding: '16px' }}>No merchant data for this hour.</div>;
                    }
                    const maxMerchantOrders = Math.max(...merchants.map(m => m.orders), 1);
                    return merchants.map((m) => {
                      const pct = (m.orders / maxMerchantOrders) * 100;
                      const hasOrders = m.orders > 0;
                      return (
                        <div
                          key={m.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '130px 1fr 30px',
                            alignItems: 'center',
                            gap: '12px',
                            fontSize: '13px'
                          }}
                        >
                          <span style={{ fontWeight: hasOrders ? '700' : '400', color: hasOrders ? '#2A2C41' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.name}
                          </span>
                          <div style={{ height: '7px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${hasOrders ? pct : 0}%`, height: '100%', background: 'linear-gradient(90deg, #F55D00 0%, #FDBF50 100%)', borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontWeight: '700', color: hasOrders ? '#2A2C41' : '#94a3b8', textAlign: 'right' }}>
                            {m.orders}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Rider Breakdown */}
              <div style={{ background: '#F4F4F8', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                  <h5 style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41', margin: 0 }}>
                    {hourlyData?.drilldown?.hour || selectedHour} - by rider
                  </h5>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#F55D00' }}>
                    {hourlyData?.drilldown?.active_riders ?? 0} riders active
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                  {(() => {
                    const riders = hourlyData?.drilldown?.by_rider || [];
                    if (riders.length === 0) {
                      return <div style={{ fontSize: '12px', color: '#2A2C41', opacity: 0.6, textAlign: 'center', padding: '16px' }}>No rider data for this hour.</div>;
                    }
                    const maxRiderOrders = Math.max(...riders.map(r => r.orders), 1);
                    return riders.map((r) => {
                      const pct = (r.orders / maxRiderOrders) * 100;
                      const hasOrders = r.orders > 0;
                      return (
                        <div
                          key={r.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '130px 1fr 30px',
                            alignItems: 'center',
                            gap: '12px',
                            fontSize: '13px'
                          }}
                        >
                          <span style={{ fontWeight: hasOrders ? '700' : '400', color: hasOrders ? '#2A2C41' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.name}
                          </span>
                          <div style={{ height: '7px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${hasOrders ? pct : 0}%`, height: '100%', background: 'linear-gradient(90deg, #F55D00 0%, #FDBF50 100%)', borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontWeight: '700', color: hasOrders ? '#2A2C41' : '#94a3b8', textAlign: 'right' }}>
                            {r.orders}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* TREND - LAST 7 DAYS */}
          {data?.trend && data.trend.length > 0 && (
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '14px', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#2A2C41' }}>
                  TREND - LAST 7 DAYS
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
                {/* Orders Bar Chart */}
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41', margin: '0 0 16px 0' }}>Orders</h4>

                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '140px', gap: '8px', paddingBottom: '26px', borderBottom: '2px solid #e2e8f0' }}>
                    {(() => {
                      const maxOrders = Math.max(...data.trend.map(t => t.orders), 1);
                      return data.trend.map(t => {
                        const isHovered = hoveredOrder?.date === t.date;
                        const pct = Math.max((t.orders / maxOrders) * 100, t.orders > 0 ? 8 : 3);
                        const barBg = isHovered ? '#F55D00' : '#FDBF50';
                        return (
                          <div
                            key={t.date}
                            onMouseEnter={() => setHoveredOrder(t)}
                            onMouseLeave={() => setHoveredOrder(null)}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', position: 'relative', cursor: 'pointer' }}
                          >
                            {/* Visible count above bar */}
                            <span style={{ fontSize: '11px', fontWeight: '700', color: isHovered ? '#F55D00' : '#2A2C41', marginBottom: '4px' }}>
                              {t.orders}
                            </span>

                            {/* Dark Floating Tooltip directly above hovered bar */}
                            {isHovered && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  background: '#2A2C41',
                                  color: '#fff',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                                  zIndex: 20,
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  border: '1px solid #F55D00',
                                  textAlign: 'center'
                                }}
                              >
                                <div style={{ fontWeight: '700', fontSize: '13px', color: '#FDBF50' }}>
                                  {t.orders} orders
                                </div>
                                <div style={{ fontSize: '10px', color: '#ffffff', opacity: 0.8, marginTop: '1px' }}>
                                  {formatFullDate(t.date)}
                                </div>
                              </div>
                            )}

                            <div
                              style={{
                                width: '100%',
                                height: `${pct}%`,
                                background: barBg,
                                borderRadius: '4px 4px 0 0',
                                transition: 'background 0.2s ease, height 0.3s ease'
                              }}
                            />
                            <span style={{ position: 'absolute', bottom: '-22px', fontSize: '11px', fontWeight: '600', color: '#2A2C41', whiteSpace: 'nowrap' }}>
                              {t.label}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Distance Bar Chart */}
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41', margin: '0 0 16px 0' }}>Distance (km)</h4>

                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '140px', gap: '8px', paddingBottom: '26px', borderBottom: '2px solid #e2e8f0' }}>
                    {(() => {
                      const maxKm = Math.max(...data.trend.map(t => t.km), 1);
                      return data.trend.map(t => {
                        const isHovered = hoveredKm?.date === t.date;
                        const pct = Math.max((t.km / maxKm) * 100, t.km > 0 ? 8 : 3);
                        const barBg = isHovered ? '#F55D00' : 'linear-gradient(180deg, #F55D00 0%, #FDBF50 100%)';
                        return (
                          <div
                            key={t.date}
                            onMouseEnter={() => setHoveredKm(t)}
                            onMouseLeave={() => setHoveredKm(null)}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', position: 'relative', cursor: 'pointer' }}
                          >
                            {/* Visible count above bar */}
                            <span style={{ fontSize: '11px', fontWeight: '700', color: isHovered ? '#F55D00' : '#2A2C41', marginBottom: '4px' }}>
                              {t.km}
                            </span>

                            {/* Dark Floating Tooltip directly above hovered bar */}
                            {isHovered && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  background: '#2A2C41',
                                  color: '#fff',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                                  zIndex: 20,
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap',
                                  pointerEvents: 'none',
                                  border: '1px solid #FDBF50',
                                  textAlign: 'center'
                                }}
                              >
                                <div style={{ fontWeight: '700', fontSize: '13px', color: '#FDBF50' }}>
                                  {t.km} km
                                </div>
                                <div style={{ fontSize: '10px', color: '#ffffff', opacity: 0.8, marginTop: '1px' }}>
                                  {formatFullDate(t.date)}
                                </div>
                              </div>
                            )}

                            <div
                              style={{
                                width: '100%',
                                height: `${pct}%`,
                                background: barBg,
                                borderRadius: '4px 4px 0 0',
                                transition: 'background 0.2s ease, height 0.3s ease'
                              }}
                            />
                            <span style={{ position: 'absolute', bottom: '-22px', fontSize: '11px', fontWeight: '600', color: '#2A2C41', whiteSpace: 'nowrap' }}>
                              {t.label}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Operational Exception Alerts */}
          {data?.exceptions && data.exceptions.length > 0 && (
            <div style={{ background: '#F4F4F8', border: '1px solid #FDBF50', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2A2C41', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaExclamationTriangle style={{ color: '#F55D00' }} /> Auto-Detected Exceptions ({data.exceptions.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.exceptions.map((ex) => (
                  <div key={ex.id} style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', color: '#2A2C41', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{ex.message}</span>
                    <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px', background: ex.severity === 'serious' ? '#F55D00' : '#FDBF50', color: ex.severity === 'serious' ? '#fff' : '#2A2C41', textTransform: 'uppercase' }}>
                      {ex.severity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Two-Column Breakdown: Leaderboard & Merchant Distribution */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Rider Leaderboard */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2A2C41', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaMedal style={{ color: '#FDBF50' }} /> Rider Leaderboard
              </h3>
              {(!data?.riderLeaderboard || data.riderLeaderboard.length === 0) ? (
                <p style={{ color: '#2A2C41', opacity: 0.6, fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>No active rider logs recorded for this date.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #F4F4F8', textAlign: 'left', color: '#2A2C41' }}>
                        <th style={{ padding: '8px' }}>Rider Name</th>
                        <th style={{ padding: '8px' }}>Status</th>
                        <th style={{ padding: '8px' }}>Orders</th>
                        <th style={{ padding: '8px' }}>KM</th>
                        <th style={{ padding: '8px' }}>OT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.riderLeaderboard.map((r, idx) => {
                        const isAvail = r.isAvailable || r.status === 'Available' || r.status === 1;
                        return (
                          <tr key={r.riderId} style={{ borderBottom: '1px solid #F4F4F8' }}>
                            <td style={{ padding: '10px 8px', fontWeight: '600', color: '#2A2C41' }}>
                              {idx === 0 && '🥇 '}
                              {idx === 1 && '🥈 '}
                              {idx === 2 && '🥉 '}
                              {r.riderName || r.name || `Rider #${r.riderId}`}
                            </td>
                            <td style={{ padding: '10px 8px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  background: isAvail ? '#F4F4F8' : '#F4F4F8',
                                  color: isAvail ? '#F55D00' : '#2A2C41',
                                  border: isAvail ? '1px solid #F55D00' : '1px solid #e2e8f0',
                                  display: 'inline-block'
                                }}
                              >
                                {isAvail ? 'Available' : 'Offline'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 8px', fontWeight: '800', color: '#2A2C41' }}>{r.orders}</td>
                            <td style={{ padding: '10px 8px', color: '#2A2C41', opacity: 0.8 }}>{r.km ?? '-'} km</td>
                            <td style={{ padding: '10px 8px', color: '#2A2C41', opacity: 0.8 }}>{r.otHours ?? '-'} hrs</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Merchant Distribution */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2A2C41', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaStore style={{ color: '#F55D00' }} /> Merchant Order Distribution
              </h3>
              {(!data?.merchantBreakdown || data.merchantBreakdown.length === 0) ? (
                <p style={{ color: '#2A2C41', opacity: 0.6, fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>No merchant order attributions recorded.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {data.merchantBreakdown.map((m) => (
                    <div key={m.merchantId} style={{ background: '#F4F4F8', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#2A2C41', marginBottom: '6px' }}>
                        <span>{m.merchantName}</span>
                        <span style={{ fontWeight: '700', color: '#F55D00' }}>{m.orders} orders ({m.sharePct}%)</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${m.sharePct}%`, height: '100%', background: 'linear-gradient(90deg, #F55D00 0%, #FDBF50 100%)', borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Fleet Operational Data Grid Section (At Bottom) */}
      <div style={{ background: isAddOrderActive ? 'transparent' : '#fff', borderRadius: '12px', border: isAddOrderActive ? 'none' : '1px solid #e2e8f0', padding: isAddOrderActive ? '0' : '20px', marginBottom: '24px', boxShadow: isAddOrderActive ? 'none' : '0 2px 6px rgba(0,0,0,0.03)' }}>
        <FleetDataEntry
          key={refreshKey}
          dateFrom={dateFrom}
          dateTo={dateTo}
          hideHeader={true}
          onOrderCreated={() => fetchFleetStats(dateFrom, dateTo)}
          onViewModeChange={(mode) => setIsAddOrderActive(mode !== 'grid')}
        />
      </div>
    </div>
  );
};

export default FleetAnalytics;
